import { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Alert } from 'assets/icons/Alert.svg';
import { ReactComponent as Copy } from 'assets/icons/Copy.svg';
import { ReactComponent as Tick } from 'assets/icons/Tick.svg';
import useToast from 'contexts/ToastProvider/useToast';
import useCopy from 'hooks/useCopy';
import testingConstants from 'utils/testingConstants';

import ApiKeyResultPopup from './ApiKeyResultPopup';

type ApiKeySecretModalVariant = 'create' | 'rotate';

interface ApiKeyCreatedModalProps {
  /** The one-time raw secret key — held only in modal-local state (NFR-01). */
  apiKey: string;
  /** Invoked on Finish so the caller can refresh the Active list. */
  onFinish?: () => void;
  /** Selects create vs rotate copy while reusing the same secret-display shell. */
  variant?: ApiKeySecretModalVariant;
}

/**
 * Shows the one-time API key after a successful create or rotate (Figma 6392:67419).
 * The raw key is never persisted — it lives only in this modal's props until close.
 */
const ApiKeyCreatedModal = create<ApiKeyCreatedModalProps>(({ apiKey, onFinish, variant = 'create' }) => {
  const { t } = useTranslation();
  const { remove } = useModal(ApiKeyCreatedModal);
  const { pushToast } = useToast();
  const { isCopied, copy } = useCopy(apiKey);
  const copyKeyPrefix = variant === 'rotate' ? 'rotated' : 'created';

  const handleCopy = () => {
    if (isCopied) return;
    void copy()
      .then(() => pushToast({ variant: 'success', icon: Tick, title: t('copied') }))
      .catch(() => pushToast({ variant: 'info', title: t('workspaces_settings.api_keys.copy_failed') }));
  };

  const handleFinish = () => {
    remove();
    onFinish?.();
  };

  return (
    <ApiKeyResultPopup
      title={t(`workspaces_settings.api_keys.${copyKeyPrefix}.title`)}
      subtitle={t(`workspaces_settings.api_keys.${copyKeyPrefix}.subtitle`)}
      finishLabel={t('workspaces_settings.api_keys.finish')}
      closeLabel={t('close')}
      onFinish={handleFinish}
      dataTestId={testingConstants.apiKeyCreatedModal}
      finishTestId={testingConstants.apiKeyCreatedFinishButton}
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-2">
          <span className="text-grayscale-black text-size-sm font-medium">
            {t(`workspaces_settings.api_keys.${copyKeyPrefix}.secret_label`)}
          </span>
          <div className="border-gray-40 flex w-full items-center gap-2 rounded-2xl border bg-white py-1 pl-4 pr-1">
            <span
              className="text-grayscale-black min-w-0 flex-1 truncate text-size-md font-light"
              data-testid={testingConstants.apiKeyCreatedSecret}
            >
              {apiKey}
            </span>
            <Button
              variant="lightTextButton"
              size="sm"
              onClick={handleCopy}
              className="!px-4 !py-2.5"
              iconRight={{ icon: isCopied ? Tick : Copy, className: 'stroke-current stroke-1.5' }}
              data-testid={testingConstants.apiKeyCreatedCopyButton}
            >
              {t(`workspaces_settings.api_keys.${copyKeyPrefix}.copy`)}
            </Button>
          </div>
        </div>

        <div className="border-secondary-50 bg-secondary-10 flex gap-2 rounded-2xl border p-3">
          <Alert className="text-secondary-50 h-5 w-5 shrink-0 stroke-current stroke-2" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-grayscale-black text-size-sm font-light">
              {t(`workspaces_settings.api_keys.${copyKeyPrefix}.warning_title`)}
            </span>
            <span className="text-gray-80 text-size-xs font-light">
              {t(`workspaces_settings.api_keys.${copyKeyPrefix}.warning_body`)}
            </span>
          </div>
        </div>
      </div>
    </ApiKeyResultPopup>
  );
});

export default ApiKeyCreatedModal;
